import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import toast from "react-hot-toast";
import { IOrderAddress } from "../../models/Order.model";
import { InvoiceData } from "../../models/invoice/invoice.model";
import { RootDispatch } from "../../store";
import { getInvoiceById } from "../../store/slices/einvoiceSlice";
import { InvoiceDocument } from "./InvoiceDocument";

type InvoicePdfButtonProps = {
  invoiceId: string;
  mode: "open" | "download";
  invoiceType?: "ORDER" | "EPC";
  shippingAddress?: IOrderAddress;
  fallbackCustomerName?: string;
  disabled?: boolean;
  className: string;
  onComplete?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  children: (loading: boolean) => React.ReactNode;
};

type InvoicePdfActionOptions = {
  dispatch: RootDispatch;
  invoiceId: string;
  mode: "open" | "download";
  invoiceType?: "ORDER" | "EPC";
  shippingAddress?: IOrderAddress;
  fallbackCustomerName?: string;
  previewWindow?: Window | null;
};

export const runInvoicePdfAction = async ({
  dispatch,
  invoiceId,
  mode,
  invoiceType = "ORDER",
  shippingAddress,
  fallbackCustomerName,
  previewWindow: suppliedPreviewWindow,
}: InvoicePdfActionOptions): Promise<boolean> => {
  let previewWindow = suppliedPreviewWindow;
  try {
    if (!invoiceId) {
      previewWindow?.close();
      toast.error("This invoice does not have an invoice ID.");
      return false;
    }

    if (previewWindow === undefined) {
      previewWindow = window.open("", "_blank");
    }
    if (mode === "open" && !previewWindow) {
      toast.error("Allow pop-ups to open invoices.");
      return false;
    }

    const response = await dispatch(getInvoiceById({ invoiceId })).unwrap();
    if (response.success === false || !response.data) {
      throw new Error(response.message || "Unable to load this invoice.");
    }

    const invoiceData = response.data as InvoiceData;
    const blob = await pdf(
      <InvoiceDocument
        data={invoiceData}
        shippingAddress={shippingAddress}
        invoiceType={invoiceType}
      />,
    ).toBlob();
    const fileUrl = URL.createObjectURL(blob);

    if (previewWindow) {
      previewWindow.location.href = fileUrl;
    }

    if (mode === "download") {
      const buyer =
        invoiceData.extractedInvoice?.BuyerDtls ||
        invoiceData.taxProResponse?.ExtractedSignedInvoiceData?.BuyerDtls;
      const customerName = (
        buyer?.LglNm ||
        buyer?.TrdNm ||
        fallbackCustomerName ||
        "Customer"
      )
        .replace(/[<>:"/\\|?*]/g, "")
        .split("")
        .filter((character) => character.charCodeAt(0) >= 32)
        .join("")
        .trim()
        .replace(/\s+/g, "_") || "Customer";
      const invoiceNumber =
        invoiceData.invoiceNumber ||
        invoiceData.extractedInvoice?.DocDtls?.No ||
        (invoiceType === "EPC" ? "EPC" : "ORD");
      const fileName = `Invoice_${customerName}_${invoiceNumber}.pdf`;
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !(window as Window & { MSStream?: unknown }).MSStream;

      if (!isIOS) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (!previewWindow) {
        window.location.href = fileUrl;
      }
    }

    window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
    return true;
  } catch (error: unknown) {
    console.error("Failed to open invoice PDF:", error);
    previewWindow?.close();
    toast.error(
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Unable to open the invoice. Please try again.",
    );
    return false;
  }
};

const InvoicePdfButton: React.FC<InvoicePdfButtonProps> = ({
  invoiceId,
  mode,
  invoiceType = "ORDER",
  shippingAddress,
  fallbackCustomerName,
  disabled = false,
  className,
  onComplete,
  onLoadingChange,
  children,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setLoading(true);
    onLoadingChange?.(true);
    const succeeded = await runInvoicePdfAction({
      dispatch,
      invoiceId,
      mode,
      invoiceType,
      shippingAddress,
      fallbackCustomerName,
    });
    setLoading(false);
    onLoadingChange?.(false);
    if (succeeded) onComplete?.();
  };

  return (
    <button
      type="button"
      disabled={disabled || loading}
      onClick={(event) => void handleClick(event)}
      className={className}
    >
      {children(loading)}
    </button>
  );
};

export default InvoicePdfButton;
